import { useEffect, useMemo, useRef, useState } from "react";
import { FormError } from "@/Components/FormError";
import { Input } from "@/Components/Form";
import Mexp from "math-expression-evaluator";
import { IconEnter } from "@/Icons";

export function LabeledInputCalculatable({
  title,
  help,
  unit,
  unitClass = "",
  inputClasses,
  containerClass,
  focus = false,
  ...props
}) {
  let [focused, setFocused] = useState(false);

  const ref = useRef(null);

  const mexp = useMemo(() => new Mexp(), []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const calculateValue = (value) => {
    if (value === "") return value;

    try {
      return mexp.eval(value);
    } catch (e) {
      return null;
    }
  };

  const hasMathExpression = (value) => {
    return !/^[0-9,.]+$/.test(value);
  };

  const setCalculatedValue = () => {
    if (
      props?.value &&
      hasMathExpression(props.value) &&
      calculateValue(props.value)
    ) {
      props.onChange({
        target: {
          name: props.name,
          value: calculateValue(props.value).toString(), // Most inputs expect a string
        },
      });
    }
  };

  return (
    <div ref={ref} className={containerClass}>
      <label className="relative block">
        {title ? <span className="mb-1 block text-[14px]">{title}</span> : null}
        <Input
          className={`relative ${
            focus && !focused && "bg-cyan-50 ring-1"
          } ${inputClasses}`}
          type="text"
          onFocus={() => setFocused(true)}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              setCalculatedValue();
            }

            // Commas are not allowed in math expressions so replace them with dots
            if (e.key === ",") {
              e.target.value = e.target.value.replace(",", ".");
            }
          }}
          {...props}
        />
        {unit && (
          <span
            className={`absolute right-2 bottom-3 text-typo-inactive ${unitClass}`}
          >
            {unit}
          </span>
        )}
        {focused && props?.value && hasMathExpression(props.value) && (
          <div className="absolute bottom-0 z-10 w-full w-max translate-y-full border border-borderColor bg-white p-6 text-typo shadow-md">
            <button
              onClick={() => setCalculatedValue()}
              className="flex flex-col md:flex-row"
              title="Press enter or click here to apply the calculated value"
            >
              {props.value}
              <strong className="font-bold">
                &nbsp;=&nbsp;
                {calculateValue(props.value) || "Invalid expression"}
              </strong>
              {calculateValue(props.value) && (
                <>
                  &nbsp;
                  <IconEnter className="text-primary" />
                </>
              )}
            </button>
          </div>
        )}
      </label>
      {help ? <p className="mt-1 text-sm text-gray-600">{help}</p> : null}

      {props?.error ? <FormError error={props?.error} /> : null}
    </div>
  );
}
